import React from "react";
import { transparentize } from "polished";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { CopyIcon, InfoIcon } from "@storybook/icons";
import {
  TooltipMessage,
  TooltipNote,
  WithTooltip,
} from "storybook/internal/components";
import { styled } from "storybook/theming";
import { useFilteredTokens } from "../hooks/useFilteredTokens";
import { createPortal } from "react-dom";

import { Category } from "../types/category.types";
import { ClipboardButton } from "./ClipboardButton";
import { PresenterMapType, TokenPreview } from "./TokenPreview";
import { TokenValue } from "./TokenValue";
import { ToolButton } from "./ToolButton";
import { Popup } from "./Popup";
import { usePopup } from "../hooks/usePopup";
import { Token } from "src/types/token.types";

interface TokenTableProps {
  categories: Category[];
  maxHeight?: number;
  readonly?: boolean;
  showValueColumn?: boolean;
  presenters?: PresenterMapType;
  filterNames?: string[];
  theme?: string;
  usageMap?: Record<string, string[]>;
}

export const TokenTable = ({
  categories,
  maxHeight,
  readonly,
  showValueColumn = true,
  presenters,
  filterNames,
  theme,
  usageMap,
}: TokenTableProps) => {
  const [tokenValueOverwrites, setTokenValueOverwrites] = useState<{
    [tokenName: string]: any;
  }>({});
  const [panelHeight, setPanelHeight] = useState<number>(maxHeight || 100);
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

  const { popup, popupRef, handleContextMenu } = usePopup({
    usageMap,
    getElementRef: (item: { token: Token; index: number }) =>
      rowRefs.current.get(item.index) || null,
    getTokenName: (item: { token: Token; index: number }) => item.token.name,
  });

  const parentRef = useRef<HTMLDivElement | null>(null);
  const theadRef = useRef<HTMLTableSectionElement | null>(null);

  const tokens = useFilteredTokens(categories, filterNames, theme);

  const rowVirtualizer = useVirtualizer({
    count: tokens.length,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback(() => 49, []),
  });

  const ScrollContainer = useMemo(
    () =>
      styled.div(() => ({
        maxHeight: panelHeight ? `${panelHeight + 30}px` : "none",
        overflow: "auto",
        padding: "15px",
      })),
    [panelHeight]
  );

  const Table = useMemo(
    () =>
      styled.table(({ theme }) => ({
        borderCollapse: "collapse",
        borderSpacing: 0,
        color: theme.color.defaultText,
        fontFamily: theme.typography.fonts.base,
        fontSize: theme.typography.size.s1,
        minWidth: 700,
        tableLayout: "fixed",
        textAlign: "left",
        width: "100%",

        "thead > tr": {
          display: "flex",
        },

        "tbody > tr": {
          borderTop: `1px solid ${theme.color.mediumlight}`,
          display: "flex",

          ":first-of-type": {
            borderTopColor: theme.color.medium,
          },

          ":last-of-type": {
            borderBottom: `1px solid ${theme.color.mediumlight}`,
          },

          ":hover": {
            backgroundColor: theme.background.hoverable,
          },
        },

        tr: {
          ":hover": {
            backgroundColor: "rgba(0,0,0, 0.1)",
          },
        },
        "td, th": {
          border: "none !important",
          textOverflow: "ellipsis",
          verticalAlign: "middle",

          ":nth-of-type(1)": {
            flexBasis: "50%",
            flexGrow: 1,
            flexShrink: 0,
          },

          ":nth-of-type(2)": {
            flexBasis: "25%",
            flexGrow: 0,
            flexShrink: 0,
          },

          ":nth-of-type(3)": {
            flexBasis: "25%",
            flexGrow: 0,
            flexShrink: 0,
          },
        },

        th: {
          color:
            theme.base === "light"
              ? transparentize(0.25, theme.color.defaultText)
              : transparentize(0.45, theme.color.defaultText),
          paddingBottom: 12,

          ":not(:first-of-type)": {
            paddingLeft: 15,
          },

          ":not(:last-of-type)": {
            paddingRight: 15,
          },

          ":last-of-type": {
            width: 200,
          },
        },

        td: {
          overflow: "hidden",
          paddingBottom: 8,
          paddingTop: 8,
          alignItems: "center",

          ":not(:first-of-type)": {
            paddingLeft: 15,
          },

          ":not(:last-of-type)": {
            paddingRight: 15,
          },

          svg: {
            maxWidth: "100%",
            maxHeight: "100%",
          },

          span: {
            alignItems: "center",
            display: "flex",
            height: "100%",
          },
        },
      })),
    []
  );

  const TokenName = styled.span(({ theme }) => ({
    fontFamily: theme.typography.fonts.mono,
    fontWeight: theme.typography.weight.bold,
    fontSize: theme.typography.size.s1,
  }));

  useLayoutEffect(() => {
    const container = document.querySelector("#storybook-panel-root");
    if (!container) {
      return;
    }

    const resizeHandler = () => {
      if (maxHeight !== undefined || !container) {
        return;
      }

      const vpHeight = window.innerHeight;
      const tableTop = parentRef.current?.getBoundingClientRect().top || 0;

      const height = vpHeight - tableTop - 40;

      setPanelHeight(height);
    };

    setTimeout(() => {
      resizeHandler();
    });

    const resizeObserver = new ResizeObserver(resizeHandler);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Force virtualizer to recalculate after mount - fixes Firefox rendering issue
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      rowVirtualizer.measure();
    });
    return () => cancelAnimationFrame(frame);
  }, [tokens]);

  return (
    <ScrollContainer ref={parentRef}>
      <Table
        style={{
          height: `${
            rowVirtualizer.getTotalSize() +
            (theadRef.current?.getBoundingClientRect().height || 0)
          }px`,
          position: "relative",
        }}
      >
        <thead
          className="docblock-argstable-head"
          ref={theadRef}
          style={{ height: "28px" }}
        >
          <tr>
            <th>Name</th>
            {showValueColumn && <th>Value</th>}
            <th>Preview</th>
          </tr>
        </thead>
        <tbody>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const token = tokens[virtualRow.index];

            return (
              <tr
                key={virtualRow.index}
                ref={(el) => {
                  if (el) rowRefs.current.set(virtualRow.index, el);
                }}
                onContextMenu={(e) =>
                  handleContextMenu(e, { token, index: virtualRow.index })
                }
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${
                    virtualRow.start +
                    (theadRef.current?.getBoundingClientRect().height || 0)
                  }px)`,
                }}
              >
                <td>
                  <span>
                    <TokenName>{token.name}</TokenName>

                    <WithTooltip
                      hasChrome={false}
                      tooltip={<TooltipNote note="Copy to clipboard" />}
                    >
                      <ClipboardButton
                        button={
                          <ToolButton>
                            <CopyIcon />
                          </ToolButton>
                        }
                        value={token.name}
                      />
                    </WithTooltip>

                    {token.description && (
                      <WithTooltip
                        tooltip={<TooltipMessage desc={token.description} />}
                      >
                        <ToolButton>
                          <InfoIcon />
                        </ToolButton>
                      </WithTooltip>
                    )}
                  </span>
                </td>
                {showValueColumn && (
                  <td>
                    <TokenValue
                      key={token.name}
                      onValueChange={(newValue) => {
                        setTokenValueOverwrites((tokenValueOverwrites) => ({
                          ...tokenValueOverwrites,
                          [token.name]:
                            newValue === token.rawValue ? undefined : newValue,
                        }));
                      }}
                      readonly={readonly}
                      token={token}
                    />
                  </td>
                )}
                <td>
                  <TokenPreview
                    presenters={presenters}
                    token={{
                      ...token,
                      value: tokenValueOverwrites[token.name] || token.value,
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {popup &&
        usageMap &&
        createPortal(
          <Popup ref={popupRef} style={{ top: popup.top, left: popup.left }}>
            {usageMap[popup.tokenName] ? (
              <>
                <div>Used in:</div>
                <ul>
                  {usageMap[popup.tokenName].map((usage, index) => (
                    <li key={index}>{usage}</li>
                  ))}
                </ul>
              </>
            ) : (
              <div>Token might be unused or global, please check</div>
            )}
          </Popup>,
          document.body
        )}
    </ScrollContainer>
  );
};
